import React, { useState, useEffect, useRef, ReactNode, MouseEvent, useMemo } from 'react';
import './Menu.css';

export interface IMenu {
  label: string | ReactNode;
  action?: (data: any) => void;
  subList?: IMenu[];
  postFixComponent?: (index: number, item: IMenu, isOpen: boolean) => ReactNode;
  id?: string;
  value?: string; // Optional parameter for additional item identification
  data?: any; // Allow extra parameters for actions or other purposes
  testId?: string; // data-test-id for testing, allows for easier testing of components
  key?: string; // Optional key for unique identification
  version?: string; // Optional version for tracking changes
}

interface IMenuItemProps {
  item: IMenu;
  type: 'click' | 'hover';
  subMenuPosition: 'left' | 'right';
  isOpen: boolean;
  onToggle: (index: number, isOpen: boolean) => void;
  index: number;
  reset: () => void;
  testId?: string;
  width?: string; // Optional width for sub-menu items
  maxHeight?: string; // Optional max-height for menu items
  version?: string; // Optional version for tracking changes
  customFunction?: any; // Optional custom function for menu items
}

export const MAX_DEPTH = 5; // Maximum allowable depth for sub-menus

/**
 * Recursively calculates the maximum depth of sub-lists.
 * 
 * @param menu The menu item to calculate depth for.
 * @returns The depth of the menu item’s sub-lists.
 */
const getSubListDepth = (menu: IMenu): number => {
  // If there are no sub-lists, the depth is 1
  if (!menu.subList || menu.subList.length === 0) return 1;
  // Recursively find the maximum depth among the sub-lists
  return 1 + Math.max(...menu.subList.map(getSubListDepth));
};

const MenuItem: React.FC<IMenuItemProps> = ({
  item,
  type,
  subMenuPosition,
  isOpen,
  onToggle,
  index,
  reset,
  width,
  maxHeight,
  version,
  customFunction
}) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  /**
   * Toggles the open state for the current menu item.
   * 
   * @param index The index of the menu item.
   * @param isOpen Whether the menu item is open.
   */
  const handleToggle = (index: number, isOpen: boolean) => {
    setOpenedIndex(isOpen ? index : null);
  };

  const dropdownRef = useRef<HTMLLIElement | null>(null);

  /**
   * Handles the mouse enter event for hover type menus.
   * Opens the dropdown menu if the type is 'hover'.
   */
  const handleMouseEnter = () => {
    if (type === 'hover') {
      onToggle(index, true);
      setIsDropdownOpen(true);
      customFunction && customFunction();
    }
  };

  /**
   * Handles the mouse leave event for hover type menus.
   * Closes the dropdown menu if the type is 'hover'.
   */
  const handleMouseLeave = () => {
    if (type === 'hover') {
      onToggle(index, false);
      setIsDropdownOpen(false);
    }
  };

  /**
   * Handles the click event for menu items.
   * Toggles sub-menu visibility if applicable, or triggers an action if no sub-lists are present.
   * 
   * @param e The mouse event that triggered the function.
   */
  const handleClick = (e: MouseEvent<HTMLLIElement>) => {
    e.stopPropagation(); // Prevent event propagation to parent elements
    if (type === 'click' && item.subList?.length) {
      // Toggle the menu open state on click if it has sub-lists
      onToggle(index, !isOpen);
      setIsDropdownOpen(!isDropdownOpen);
      setOpenedIndex(null);
      customFunction && customFunction();
    }
    if (!item?.subList?.length && typeof item.action === 'function') {
      // Call the action function if the item has no sub-lists
      item.action({ ...item, index });
      reset(); // Reset the menu state after action
    }
  };

  return (
    <li
      className={`menu-item ${isOpen ? 'menu-item--active' : ''}`}
      ref={dropdownRef}
      id={item.id} // Apply id to the menu item for unique identification
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      data-test-id={item.testId} // Set data-test-id for testing
      tabIndex={1} // Make the menu item focusable
      aria-expanded={isOpen} // Indicates whether the item is expanded
      role="button"
    >
      <a className={`menu-option ${isOpen ? 'active' : ''}`}>
        <div className={version === "v2" && `menu-option-label`}>
          {item.label}
        </div>
        {item.postFixComponent && (
          <div className='post-fix'>
            {item.postFixComponent(index, item, isOpen)}
          </div>
        )}
      </a>
      {(isOpen && item?.subList?.length) ? (
        <ul
          className={`menu-content ${subMenuPosition}`} // Position sub-menu based on 'left' or 'right'
          style={{ width: width || "300px", ...(version === "v2" && maxHeight && { maxHeight: maxHeight }) }}
          data-test-id={`${item.testId}-child`} // Set data-test-id for child menu items
        >
          {item.subList.map((subItem, subIndex) => (
            <MenuItem
              key={subItem.key || `menu-item-${index}-${subIndex}`} // Use key if present, otherwise use index
              item={subItem}
              isOpen={openedIndex === subIndex}
              type={type}
              subMenuPosition={subMenuPosition}
              onToggle={handleToggle}
              index={subIndex}
              reset={reset}
              width={width}
              maxHeight={maxHeight}
              version={version}
              customFunction={customFunction}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

export interface IMenuProps {
  list: IMenu[];
  type: 'click' | 'hover';
  subMenuPosition: 'left' | 'right';
  children: ReactNode;
  testId?: string;
  width?: string; // Optional width for the main menu
  version?: string; // Optional version for tracking changes
  maxHeight?: string; // Optional max-height for menu items
  customFunction?: any; // Optional trigger function for menu items
}

const Menu: React.FC<IMenuProps> = ({
  list,
  type,
  subMenuPosition,
  children,
  testId,
  width,
  maxHeight,
  version,
  customFunction
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  /**
   * Resets the menu state by closing the dropdown and clearing the opened index.
   */
  const reset = () => {
    setIsDropdownOpen(false);
    setOpenedIndex(null);
  };

  /**
   * Handles mouse enter event for hover type menus.
   * Opens the dropdown menu if the type is 'hover'.
   */
  const handleMouseEnter = () => {
    if (type === 'hover') {
      setIsDropdownOpen(true);
    }
  };

  /**
   * Handles mouse leave event for hover type menus.
   * Closes the dropdown menu if the type is 'hover'.
   */
  const handleMouseLeave = () => {
    if (type === 'hover') {
      setIsDropdownOpen(false);
    }
  };

  /**
   * Handles click event for the main menu.
   * Toggles the visibility of the main dropdown.
   * 
   * @param e The mouse event that triggered the function.
   */
  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent event propagation to parent elements
    setIsDropdownOpen(!isDropdownOpen);
    setOpenedIndex(null);
  };

  /**
   * Handles clicks outside the menu to close it.
   * 
   * @param e The mouse event that triggered the function.
   */
  const handleClickOutside = (e: Event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsDropdownOpen(false);
      setOpenedIndex(null);
    }
  };

  /**
   * Handles toggling of menu items.
   * 
   * @param index The index of the menu item.
   * @param isOpen Whether the menu item is open.
   */
  const handleToggle = (index: number, isOpen: boolean) => {
    setOpenedIndex(isOpen ? index : null);
  };

  // Effect to add/remove event listeners for click and keydown
  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside as EventListener);
      document.addEventListener('keydown', handleClickOutside as EventListener);
    } else {
      document.removeEventListener('click', handleClickOutside as EventListener);
      document.removeEventListener('keydown', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside as EventListener);
      document.removeEventListener('keydown', handleClickOutside as EventListener);
    };
  }, [isDropdownOpen]);

  /**
   * Calculates the maximum depth of the menu's sub-lists.
   * 
   * @returns The maximum depth of the menu.
   */
  const overallDepth = useMemo(() => Math.max(...list.map(getSubListDepth)), [list]);

  /**
   * Warns if the depth exceeds the maximum limit.
   */
  useEffect(() => {
    if (overallDepth > MAX_DEPTH) {
      console.warn(`Warn: SubList depth exceeds the maximum limit of ${MAX_DEPTH}`);
    }
  }, [overallDepth]);

  return (
    <div
      className={`menu ${version === "v2" ? "menu__v2" : ""}`} // Apply style based on version
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={dropdownRef}
      onClick={handleClick}
      data-test-id={testId || "menu"} // Set data-test-id for the main menu
      tabIndex={0} // Make the menu item focusable
      aria-expanded={isDropdownOpen} // Indicates whether the item is expanded
      role='menu'
    >
      <div>{children}</div>
      {isDropdownOpen && (
        <ul
          className={`menu-content menu-content-${type}`} // Apply style based on menu type
          style={{ width: width || "293px", ...(version === "v2" && maxHeight && { maxHeight: maxHeight }) }} // Default width if none provided
          data-test-id='menu-child' // Set data-test-id for child menu items
        >
          {list.map((item, index) => (
            <MenuItem
              key={item.key || `menu-item-${index}`} // Use key if present, otherwise use index
              item={item}
              type={type}
              subMenuPosition={subMenuPosition}
              isOpen={openedIndex === index}
              onToggle={handleToggle}
              index={index}
              width={width}
              reset={reset}
              maxHeight={maxHeight}
              version={version}
              customFunction={customFunction}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default Menu;
